import React from "react";
import { cn } from "../../../common/u1/cn";
import { CompanyMark } from "../../../common/u1/CompanyMark";
import { getCountryByCode } from "../../../../utils/Helper";
import { formatDateWithOrdinal } from "../../../../global_functions/global";
import { ArrowRight, BuildingIcon, DocumentIcon } from "../../../../assets/ui/icons";
import { ProfileCard, ProfileField, ProfileTable, TD, Pill } from "./ProfilePrimitives";
import {
  hasRegistration,
  permittedActivities,
  stakeholderRows,
  stakeholderSegments,
  signatoryRows,
  personnelRows,
  relationRows,
  relationTone,
  abbreviate,
  bankRows,
  taxRows,
  insuranceRows,
  financialAdmin,
} from "./businessDetailsFields";

/* Tab 02 — Business Details. */

export const RegistrationDetails = ({ company }) => {
  if (!hasRegistration(company)) return null;
  const activities = permittedActivities(company?.activities);

  return (
    <ProfileCard title="Registration Details">
      <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        <ProfileField label="Registration Body">{company?.registration_body}</ProfileField>
        <ProfileField label="Legal Form">{company?.legal_form}</ProfileField>
        <ProfileField label="Trade License">{company?.license_no}</ProfileField>
        <ProfileField label="Date of Issue">
          {company?.date_of_issue ? formatDateWithOrdinal(company.date_of_issue) : null}
        </ProfileField>
        <ProfileField label="Date of Expiry">
          {company?.date_of_expiry ? formatDateWithOrdinal(company.date_of_expiry) : null}
        </ProfileField>
        <ProfileField label="Country of Incorporation">
          {company?.country_code ? (
            <span className="inline-flex items-center gap-2">
              <img
                src={`https://flagsapi.com/${company.country_code}/flat/64.png`}
                alt=""
                aria-hidden="true"
                className="h-4 w-4 object-cover"
              />
              {getCountryByCode(company.country_code)}
            </span>
          ) : null}
        </ProfileField>
        <ProfileField label="Capital">{company?.capital}</ProfileField>
      </div>

      {activities.length > 0 && (
        <div className="mt-6 border-t border-[var(--line)] pt-5">
          <p className="m-0 mb-3 text-[10.5px] font-bold uppercase tracking-[.07em] text-[var(--muted)] font-sans">
            Permitted Activities
          </p>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {activities.map((activity, i) => (
              <li
                key={`${activity.code || "act"}-${i}`}
                className="flex items-baseline gap-3 text-[13.5px] text-[var(--ink)] font-sans"
              >
                {activity.code && (
                  <span className="shrink-0 rounded bg-[var(--slate-2)] px-2 py-1 text-[12px] font-semibold text-[var(--ink-2)] font-mono">
                    {activity.code}
                  </span>
                )}
                <span className="min-w-0">{activity.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Transcribed: the licence document is a link out when the company has
          one on file. */}
      {company?.trade_license && (
        <a
          href={company.trade_license}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-2 text-[13.5px] font-semibold text-[var(--coral)] font-sans hover:text-[var(--coral-deep)]"
        >
          <DocumentIcon size={15} />
          View trade licence
        </a>
      )}
    </ProfileCard>
  );
};

export const Stakeholders = ({ company, onOpenCompany }) => {
  const rows = stakeholderRows(company);
  if (rows.length === 0) return null;
  const segments = stakeholderSegments(rows);

  return (
    <ProfileCard title="Stakeholders">
      {segments.length > 0 && (
        <div
          aria-hidden="true"
          className="mb-5 flex h-[9px] w-full overflow-hidden rounded-full bg-[var(--slate-3)]"
        >
          {segments.map((seg, i) => (
            <span
              key={`${seg.id}-${i}`}
              style={{ width: `${seg.width}%`, backgroundColor: seg.tone }}
              className="h-full"
            />
          ))}
        </div>
      )}

      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {segments.map((row, i) => (
          <li
            key={`${row.id}-${i}`}
            className={cn(
              "flex flex-wrap items-center gap-3 rounded-xl border border-[var(--line)]",
              "bg-white px-3.5 py-3"
            )}
          >
            {row.kind === "Company" ? (
              <span
                aria-hidden="true"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--coral-soft)] bg-white text-[var(--coral)]"
              >
                <BuildingIcon size={17} />
              </span>
            ) : (
              <CompanyMark company={{ name: row.name }} size="xs" shape="circle" />
            )}

            <span className="min-w-0 grow basis-[160px]">
              {/* An owner that is itself on TREVEX opens its own profile —
                  transcribed from OwnershipProfileItem, which links by the
                  row's `ownershipCompany`. */}
              {row.companyID ? (
                <button
                  type="button"
                  onClick={() => onOpenCompany?.(row.companyID)}
                  className="block text-left text-[14px] font-bold text-[var(--coral)] font-sans hover:text-[var(--coral-deep)]"
                >
                  {row.name}
                </button>
              ) : (
                <span className="block text-[14px] font-bold text-[var(--ink)] font-sans">
                  {row.name}
                </span>
              )}
            </span>

            <Pill tone={row.kind === "Company" ? "navy" : "violet"}>{row.kind}</Pill>

            <span
              aria-hidden="true"
              className="h-[7px] w-[70px] shrink-0 overflow-hidden rounded-full bg-[var(--slate-3)]"
            >
              <span
                className="block h-full rounded-full"
                style={{ width: `${row.width}%`, backgroundColor: row.tone }}
              />
            </span>

            <span className="w-[58px] shrink-0 text-right text-[14px] font-bold text-[var(--ink)] font-sans tabular-nums">
              {row.percentage.toFixed(1)}%
            </span>
          </li>
        ))}
      </ul>
    </ProfileCard>
  );
};

export const AuthorizedSignatories = ({ company }) => {
  const rows = signatoryRows(company);
  if (rows.length === 0) return null;

  return (
    <ProfileCard title="Authorized Signatories">
      <ProfileTable columns={["Name", "Title", "Email", "Mobile", "Nationality"]} minWidth="780px">
        {rows.map((row, i) => (
          <tr key={`${row.name}-${i}`}>
            <TD className="font-semibold text-[var(--ink)]">
              <span className="flex items-center gap-2.5">
                <CompanyMark company={{ name: row.name }} size="xs" shape="circle" />
                {row.name}
              </span>
            </TD>
            <TD>{row.title}</TD>
            <TD>{row.email}</TD>
            <TD className="whitespace-nowrap">{row.mobile}</TD>
            <TD>{row.nationality && <Pill>{row.nationality}</Pill>}</TD>
          </tr>
        ))}
      </ProfileTable>
    </ProfileCard>
  );
};

export const KeyPersonnel = ({ company }) => {
  const rows = personnelRows(company);
  if (rows.length === 0) return null;

  return (
    <ProfileCard title="Key Personnel">
      <ProfileTable columns={["Name", "Designation", "Mobile", "Email"]} minWidth="720px">
        {rows.map((row, i) => (
          <tr key={`${row.name}-${i}`}>
            <TD className="font-semibold text-[var(--ink)]">
              <span className="flex items-center gap-2.5">
                <CompanyMark company={{ name: row.name }} size="xs" shape="circle" />
                {row.name}
              </span>
            </TD>
            <TD>{row.designation}</TD>
            <TD className="whitespace-nowrap">{row.mobile}</TD>
            <TD>{row.email}</TD>
          </tr>
        ))}
      </ProfileTable>
    </ProfileCard>
  );
};

export const RelatedEntities = ({ company, onOpenCompany }) => {
  const rows = relationRows(company);
  if (rows.length === 0) return null;

  return (
    <ProfileCard title="Related Entities & Business Relations">
      <ProfileTable columns={["Abbr.", "Company", "Location", "Relation", ""]} minWidth="760px">
        {rows.map((row, i) => {
          const location = [row.state, row.country].filter(Boolean).join(", ");
          /* Transcribed: only a row with a TREVEX companyID behind it opens. */
          const openable = Boolean(row.companyID);

          return (
            <tr key={`${row.source}-${row.name}-${i}`}>
              <TD>
                <span className="rounded bg-[var(--slate-2)] px-2 py-1 text-[11px] font-bold tracking-[.04em] text-[var(--ink-2)] font-mono">
                  {abbreviate(row.name)}
                </span>
              </TD>
              <TD className="font-semibold text-[var(--ink)]">
                <span className="flex flex-wrap items-center gap-2">
                  {openable ? (
                    <button
                      type="button"
                      onClick={() => onOpenCompany?.(row.companyID)}
                      className="text-left font-semibold text-[var(--coral)] font-sans hover:text-[var(--coral-deep)]"
                    >
                      {row.name}
                    </button>
                  ) : (
                    row.name
                  )}
                  {openable && <Pill tone="green">On TREVEX</Pill>}
                </span>
              </TD>
              <TD>{location}</TD>
              <TD>{row.relation && <Pill tone={relationTone(row.relation)}>{row.relation}</Pill>}</TD>
              <TD className="text-right">
                {openable && (
                  <span aria-hidden="true" className="inline-flex text-[var(--muted-2)]">
                    <ArrowRight />
                  </span>
                )}
              </TD>
            </tr>
          );
        })}
      </ProfileTable>
    </ProfileCard>
  );
};

export const Banks = ({ company }) => {
  const rows = bankRows(company);
  if (rows.length === 0) return null;

  return (
    <ProfileCard title="Banks" className="h-full">
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {rows.map((bank, i) => (
          <li
            key={`${bank.name}-${i}`}
            className="flex min-h-[60px] items-center gap-3 rounded-xl bg-[var(--bg)] px-3.5 py-3"
          >
            <span
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[var(--line)] bg-white text-[var(--coral)]"
            >
              <BuildingIcon size={17} />
            </span>
            <span className="min-w-0">
              <span className="block text-[14px] font-bold text-[var(--ink)] font-sans">
                {bank.name}
              </span>
              {bank.iban && (
                <span className="mt-0.5 block break-all text-[12px] text-[var(--muted)] font-mono">
                  IBAN · {bank.iban}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </ProfileCard>
  );
};

export const TaxInformation = ({ company }) => {
  const rows = taxRows(company);
  if (rows.length === 0) return null;

  return (
    <ProfileCard title="Tax Information" className="h-full">
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {rows.map((tax, i) => (
          <li
            key={`${tax.label}-${i}`}
            className="flex min-h-[60px] flex-wrap items-center gap-3 rounded-xl bg-[var(--bg)] px-3.5 py-3"
          >
            {tax.label && (
              <Pill tone={/vat/i.test(tax.label) ? "coral" : "navy"}>{tax.label}</Pill>
            )}
            <span className="min-w-0 break-all text-[14px] font-semibold text-[var(--ink)] font-mono">
              {tax.number}
            </span>
          </li>
        ))}
      </ul>
    </ProfileCard>
  );
};

export const Insurance = ({ company }) => {
  const rows = insuranceRows(company);
  if (rows.length === 0) return null;

  return (
    <ProfileCard title="Insurance">
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
        {rows.map((row, i) => (
          <li
            key={`${row.provider}-${i}`}
            className="grid grid-cols-1 gap-4 rounded-xl bg-[var(--bg)] px-4 py-3.5 sm:grid-cols-3"
          >
            <ProfileField label="Provider">{row.provider}</ProfileField>
            <ProfileField label="Type">
              {row.type && <Pill tone="coral">{row.type}</Pill>}
            </ProfileField>
            <ProfileField label="Broker">{row.broker}</ProfileField>
          </li>
        ))}
      </ul>
    </ProfileCard>
  );
};

export const FinancialsAdmin = ({ company }) => {
  const admin = financialAdmin(company);
  if (!admin) return null;
  const hasAny = Object.values(admin).some(Boolean);
  if (!hasAny) return null;

  return (
    <ProfileCard title="Financials — Admin Info">
      <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-3 lg:grid-cols-5">
        <ProfileField label="Financial Year End">{admin.yearEnd}</ProfileField>
        <ProfileField label="Last Audited">{admin.lastAudited}</ProfileField>
        <ProfileField label="Audit Firm">{admin.auditFirm}</ProfileField>
        <ProfileField label="State">{admin.state}</ProfileField>
        <ProfileField label="Country">
          {admin.country ? getCountryByCode(admin.country) || admin.country : null}
        </ProfileField>
      </div>
    </ProfileCard>
  );
};

export const BusinessDetails = ({ company, onOpenCompany }) => (
  <div className="flex flex-col gap-[22px]">
    <RegistrationDetails company={company} />
    <Stakeholders company={company} onOpenCompany={onOpenCompany} />
    <AuthorizedSignatories company={company} />
    <KeyPersonnel company={company} />
    <RelatedEntities company={company} onOpenCompany={onOpenCompany} />

    {/* Banks and Tax pair up, as the design places them. */}
    <div className="grid grid-cols-1 gap-[22px] lg:grid-cols-2">
      <Banks company={company} />
      <TaxInformation company={company} />
    </div>

    <Insurance company={company} />
    <FinancialsAdmin company={company} />
  </div>
);

export default BusinessDetails;
